import React, { useState } from 'react';
import { X, Loader2, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import { sendFeedbackEmail } from '../lib/email';
import { supabase } from '../lib/supabase';

type FeedbackType = 'feature' | 'feedback' | 'bug';

interface FeedbackModalProps {
  onClose: () => void;
}

const TYPE_LABELS: Record<FeedbackType, string> = {
  feature: 'Feature Request',
  feedback: 'General Feedback',
  bug: 'Bug Report',
};

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const [type, setType] = useState<FeedbackType>('feedback');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userEmail = user?.email || 'Unknown';

      await sendFeedbackEmail({
        type: TYPE_LABELS[type],
        subject: subject.trim() || TYPE_LABELS[type],
        message: message.trim(),
        userEmail,
      });

      setSent(true);
    } catch (err) {
      console.error('Error sending feedback:', err);
      setError('Failed to send feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-sand/20 dark:border-white/10">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-electric" />
            <h2 className="text-lg font-semibold text-midnight dark:text-soft-white">Send Feedback</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate dark:text-soft-white/60 hover:text-midnight dark:hover:text-soft-white rounded-lg p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {sent ? (
          <div className="px-6 py-10 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-emerald/10 text-emerald rounded-full mb-4">
              <CheckCircle className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-midnight dark:text-soft-white mb-2">Thanks for your feedback!</h3>
            <p className="text-slate dark:text-soft-white/70 text-sm mb-6">
              We read every submission and use it to make SermonBuild better.
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Type selector */}
            <div>
              <label className="block text-sm font-medium text-slate dark:text-soft-white/80 mb-2">
                Type
              </label>
              <div className="flex gap-2">
                {(['feedback', 'feature', 'bug'] as FeedbackType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-medium border transition-colors ${
                      type === t
                        ? 'bg-electric text-white border-electric'
                        : 'border-sand/40 dark:border-white/10 text-slate dark:text-soft-white/70 hover:border-electric/50'
                    }`}
                  >
                    {TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-slate dark:text-soft-white/80 mb-1">
                Subject <span className="text-slate/50 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={`Brief summary of your ${type === 'bug' ? 'issue' : type === 'feature' ? 'idea' : 'feedback'}`}
                className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-white/10 rounded-lg bg-white dark:bg-charcoal/50 text-midnight dark:text-soft-white placeholder-slate/40 focus:ring-2 focus:ring-electric focus:border-transparent"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-slate dark:text-soft-white/80 mb-1">
                Message <span className="text-crimson">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  type === 'bug'
                    ? 'Describe what happened and the steps to reproduce it...'
                    : type === 'feature'
                    ? 'Describe the feature and how it would help your workflow...'
                    : 'Share your thoughts, ideas, or suggestions...'
                }
                rows={5}
                required
                className="w-full px-3 py-2 text-sm border border-sand/40 dark:border-white/10 rounded-lg bg-white dark:bg-charcoal/50 text-midnight dark:text-soft-white placeholder-slate/40 focus:ring-2 focus:ring-electric focus:border-transparent resize-none"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-crimson/10 text-crimson rounded-lg text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-slate dark:text-soft-white/70 hover:text-midnight dark:hover:text-soft-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !message.trim()}
                className="px-5 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
